import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Menu, X, User, Heart, LogOut, Settings, ChefHat } from 'lucide-react';

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, userProfile, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="bg-bone-DEFAULT/80 backdrop-blur-md border-b border-navy/5 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <ChefHat className="w-8 h-8 text-terracotta" />
            <span className="font-display text-xl font-bold text-navy">Schmackofatz</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link 
              to="/" 
              className="text-navy hover:text-terracotta transition-colors font-medium"
              data-testid="nav-home"
            >
              Home
            </Link>
            <Link 
              to="/rezepte" 
              className="text-navy hover:text-terracotta transition-colors font-medium"
              data-testid="nav-recipes"
            >
              Rezepte
            </Link>
            <Link 
              to="/kontakt" 
              className="text-navy hover:text-terracotta transition-colors font-medium"
              data-testid="nav-contact"
            >
              Kontakt
            </Link>
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="flex items-center gap-2 hover:bg-terracotta/10"
                    data-testid="nav-user-menu"
                  >
                    {userProfile?.photoURL ? (
                      <img 
                        src={userProfile.photoURL} 
                        alt="Profile" 
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-terracotta flex items-center justify-center">
                        <User className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <span className="text-navy font-medium">
                      {userProfile?.displayName || user.email?.split('@')[0]}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to="/profil" className="flex items-center gap-2" data-testid="nav-profile">
                      <User className="w-4 h-4" />
                      Mein Profil
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/favoriten" className="flex items-center gap-2" data-testid="nav-favorites">
                      <Heart className="w-4 h-4" />
                      Favoriten
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center gap-2" data-testid="nav-admin">
                          <Settings className="w-4 h-4" />
                          Admin
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="text-red-500 cursor-pointer"
                    data-testid="nav-logout"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    className="text-navy hover:text-terracotta"
                    data-testid="nav-login"
                  >
                    Anmelden
                  </Button>
                </Link>
                <Link to="/registrieren">
                  <Button 
                    className="bg-terracotta text-white hover:bg-terracotta/90 rounded-full px-6"
                    data-testid="nav-register"
                  >
                    Registrieren
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-navy"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-navy/5"
          >
            <div className="px-4 py-4 space-y-3">
              <Link 
                to="/" 
                className="block py-2 text-navy hover:text-terracotta font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </Link>
              <Link 
                to="/rezepte" 
                className="block py-2 text-navy hover:text-terracotta font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Rezepte
              </Link>
              <Link 
                to="/kontakt" 
                className="block py-2 text-navy hover:text-terracotta font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Kontakt
              </Link>
              
              {user ? (
                <>
                  <Link 
                    to="/profil" 
                    className="block py-2 text-navy hover:text-terracotta font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Mein Profil
                  </Link>
                  <Link 
                    to="/favoriten" 
                    className="block py-2 text-navy hover:text-terracotta font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Favoriten
                  </Link>
                  {isAdmin && (
                    <Link 
                      to="/admin" 
                      className="block py-2 text-navy hover:text-terracotta font-medium"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Admin
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="block w-full text-left py-2 text-red-500 font-medium"
                  >
                    Abmelden
                  </button>
                </>
              ) : (
                <div className="space-y-2 pt-2">
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full rounded-full">
                      Anmelden
                    </Button>
                  </Link>
                  <Link to="/registrieren" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-terracotta text-white rounded-full">
                      Registrieren
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
